"use client";

import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

export interface WizardStep {
  id: string;
  label: string;
}

interface WizardStepperProps {
  steps: WizardStep[];
  currentIndex: number;
  /** Highest step index the user has reached (for allowing back-navigation). */
  maxReachedIndex: number;
  onStepClick: (index: number) => void;
}

/**
 * Horizontal step indicator. Completed and already-visited steps are clickable
 * so users can jump back (and, when editing, jump around freely).
 */
export function WizardStepper({
  steps,
  currentIndex,
  maxReachedIndex,
  onStepClick,
}: WizardStepperProps) {
  return (
    <ol className="flex w-full items-center gap-2" aria-label="Progress">
      {steps.map((step, index) => {
        const isCurrent = index === currentIndex;
        const isComplete = index < currentIndex;
        const isReachable = index <= maxReachedIndex;

        return (
          <li key={step.id} className="flex flex-1 items-center gap-2">
            <button
              type="button"
              onClick={() => isReachable && onStepClick(index)}
              disabled={!isReachable}
              aria-current={isCurrent ? "step" : undefined}
              className={cn(
                "flex min-w-0 flex-1 items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors",
                isReachable ? "cursor-pointer hover:bg-accent" : "cursor-not-allowed opacity-60"
              )}
            >
              <span
                className={cn(
                  "flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-xs font-semibold",
                  isCurrent && "border-primary bg-primary text-primary-foreground",
                  isComplete && "border-primary bg-primary/10 text-primary",
                  !isCurrent && !isComplete && "border-border text-muted-foreground"
                )}
              >
                {isComplete ? <Check className="h-3.5 w-3.5" /> : index + 1}
              </span>
              <span
                className={cn(
                  "truncate text-sm",
                  isCurrent ? "font-medium text-foreground" : "text-muted-foreground"
                )}
              >
                {step.label}
              </span>
            </button>
            {index < steps.length - 1 && (
              <span className="hidden h-px flex-1 bg-border sm:block" aria-hidden />
            )}
          </li>
        );
      })}
    </ol>
  );
}
